import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WhiteboardItem, WhiteboardState } from "./types";
import { fetchWhiteboardItems, updateWhiteboardItem, addWhiteboardItem, deleteWhiteboardItem, moveWhiteboardItemUp, moveWhiteboardItemDown } from "./whiteboardAsynkThunk";
import { message } from "antd";
import { toast } from "react-toastify";

const initialState: WhiteboardState = {
  items: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  isModalOpen: false,
  currentItem: null,
  isEditing: false,
};

const whiteboardSlice = createSlice({
  name: "whiteboard",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<WhiteboardItem | null>) => {
      state.isModalOpen = true;
      state.currentItem = action.payload;
      state.isEditing = !!action.payload;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.currentItem = null;
      state.isEditing = false;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch whiteboard items
      .addCase(fetchWhiteboardItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWhiteboardItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchWhiteboardItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Add whiteboard item
      .addCase(addWhiteboardItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addWhiteboardItem.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentPage === 1) {
          state.items.unshift(action.payload);
        }
        state.totalCount += 1;
        state.isModalOpen = false;
        if (state.currentPage === 1) {
          toast.success("Whiteboard item added successfully");
        } else {
          toast.success("Whiteboard item added in the 1st page successfully");
        }
      })
      .addCase(addWhiteboardItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error("Failed to add whiteboard item");
      })

      // Update whiteboard item
      .addCase(updateWhiteboardItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWhiteboardItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.isModalOpen = false;
        toast.success("Whiteboard item updated successfully");
      })
      .addCase(updateWhiteboardItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error("Failed to update whiteboard item");
      })

      // Delete whiteboard item
      .addCase(deleteWhiteboardItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWhiteboardItem.fulfilled, (state) => {
        state.loading = false;
        toast.success("Whiteboard item deleted successfully");
        // state.items = state.items.filter((item) => item.id !== action.payload);
        // state.totalCount -= 1;
      })
      .addCase(deleteWhiteboardItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error("Failed to delete whiteboard item");
      })

      // Move whiteboard item up
      .addCase(moveWhiteboardItemUp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveWhiteboardItemUp.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload);
        if (index > 0) {
          // Swap with the item above
          [state.items[index], state.items[index - 1]] = [state.items[index - 1], state.items[index]];
        }
      })
      .addCase(moveWhiteboardItemUp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Move whiteboard item down
      .addCase(moveWhiteboardItemDown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveWhiteboardItemDown.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload);
        if (index !== -1 && index < state.items.length - 1) {
          // Swap with the item below
          [state.items[index], state.items[index + 1]] = [state.items[index + 1], state.items[index]];
        }
      })
      .addCase(moveWhiteboardItemDown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { openModal, closeModal, setCurrentPage, setPageSize } = whiteboardSlice.actions;

export default whiteboardSlice.reducer;
